import { MAX_FORM_HEADING_WALK_UP, MAX_HEADING_HORIZONTAL_DIST, MAX_HEADING_VERTICAL_DIST } from "@protontech/autofill/constants/heuristics";
import { kDomGroupSelector, kHeadingSelector } from "@protontech/autofill/constants/selectors";
import { getBaseAttributes, getFieldAttributes, getTextAttributes } from "./attributes";
import { getLabelFor, getRectMinDistance, matchPreviousNonEmptySibling, walkUpWhile } from "./dom";

import { sanitizeString, sanitizeStringWithSpaces } from "./text";

export const getAutocompletes = (field: HTMLElement | HTMLSelectElement): string[] => (field.getAttribute("autocomplete") ?? "").split(/\s+/);

/* extracts the page title and the possible
 * text in any `<meta name="description" />`*/
export const getPageDescriptionText = (doc: Document): string => {
  const pageTitle = doc.title;
  const metaDescription = doc.querySelector('meta[name="description"]');
  const descriptionContent = metaDescription?.getAttribute("content") ?? "";

  return sanitizeString(`${pageTitle} ${descriptionContent}`);
};

/* extracts a nodes inner text and concatenates it with
 * any text based attributes */
export const getNodeText = (node: HTMLElement): string => {
  const textAttrs = getTextAttributes(node).join("");
  return sanitizeString(`${node.innerText}${textAttrs}`);
};

export const getNodeAttributes = (node: HTMLElement): string => sanitizeStringWithSpaces(getBaseAttributes(node).join(""));
export const getAllNodeHaystacks = (node: HTMLElement) => [getNodeText(node), getNodeAttributes(node)];

/* extracts a form's text attributes and concatenates
 * them with any fieldset text attributes */
export const getFormText = (form: HTMLElement): string => {
  const textAttrs = getTextAttributes(form).join("");
  const fieldsets = Array.from(form.querySelectorAll("fieldset"));
  return sanitizeString(`${textAttrs}${fieldsets.reduce((text, fieldset) => text.concat(getTextAttributes(fieldset).join("")), "")}`);
};

/* extracts the input label's text and concatenates
 * it with any text attributes of interests */
export const getFieldLabelText = (field: HTMLElement): string => {
  const label = getLabelFor(field);
  return label ? getNodeText(label) : "";
};

/* if field being analyzed is of `[type="hidden"]` then
 * bypass the label & fieldset resolution step */
export const getFieldHaystacks = (field: HTMLElement) => {
  const isHiddenInput = field instanceof HTMLInputElement && field.type === "hidden";
  const checkLabel = field instanceof HTMLInputElement && ["text", "email", "tel", "password"].includes(field.type);

  const fieldAttrs = getFieldAttributes(field);
  const fieldText = isHiddenInput ? "" : getNodeText(field);
  const labelText = checkLabel && !isHiddenInput ? getFieldLabelText(field) : "";

  return { fieldAttrs, fieldText, labelText };
};

/* During the form detection pass we can concatenate
 * all of the field haystacks as we're more interested
 * in a non-granular fuzzy match approach */
export const getAllFieldHaystacks = (field: HTMLElement): string[] => {
  const { fieldAttrs, fieldText, labelText } = getFieldHaystacks(field);

  /** Extract haystacks from `value` attributes for input types where the value
   * itself conveys semantic meaning (e.g., button labels, option text) */
  if (field instanceof HTMLInputElement && ["checkbox", "submit", "button", "image"].includes(field.type)) {
    if (field.value) fieldAttrs.push(sanitizeString(field.value));
    if (field.type === "image" && field.alt) fieldAttrs.push(sanitizeString(field.alt));
  }

  const haystacks = [fieldText, labelText, ...fieldAttrs];
  const autocomplete = field.getAttribute("autocomplete");
  if (autocomplete) haystacks.push(sanitizeString(autocomplete));

  return haystacks;
};

/* Finds heading text near an element by walking up the DOM tree. Filters
 * headings by distance to avoid unrelated content. Stops at body or group
 * elements to prevent noise from page-level structure. Falls back to previous
 * sibling or parent's first child text if no headings found. */
export const getNearestHeadingsText = (el: HTMLElement): string => {
  const originRect = el.getBoundingClientRect();

  const parent = walkUpWhile(
    el,
    MAX_FORM_HEADING_WALK_UP,
  )((parentEl, candidate) => {
    if (parentEl === document.body) return false;
    if (candidate.matches(kDomGroupSelector)) return false;
    return true;
  });

  const headings = Array.from(parent.querySelectorAll<HTMLElement>(kHeadingSelector)).filter((heading) => {
    if (el.contains(heading)) return true;

    const headingRect = heading.getBoundingClientRect();
    const { dx, dy } = getRectMinDistance(originRect, headingRect);
    return dx < MAX_HEADING_HORIZONTAL_DIST && dy < MAX_HEADING_VERTICAL_DIST;
  });

  /* As fallback, look for the most plausible text above by considering
   * the previous sibling and the first element in the parent. */
  const textAbove = (() => {
    if (headings.length > 0) return headings.map((el) => el.innerText).join();

    const prevSibling = matchPreviousNonEmptySibling(el);
    const parentFirstChild = el.parentElement?.firstElementChild as HTMLElement;
    const prevSiblingText = (prevSibling as HTMLElement)?.innerText ?? "";
    const parentFirstChildText = parentFirstChild?.innerText ?? "";

    return prevSibling === parentFirstChild ? prevSiblingText : prevSiblingText + parentFirstChildText;
  })();

  return sanitizeString(textAbove);
};
